import CircleIcon from "@mui/icons-material/Circle";
import { Divider } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { colors } from "@zapalacorp/tokens";
import { useOperationDespatchContext } from "../../../../../../../../contexts/DespatchContext";
import { CommentsCount } from "../../../../style";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";
import { DefaultTooltip } from "Components/Generic/Tooltip";
import { VipChip } from "Components/Generic/VipChip";
import { DESTINATION_TASK_TYPES, GO_SHARE_COMPANY_IDS } from "Constants";
import { Calendar as IconCalendar, Clock as IconClock, Ship as IconShip } from "iconsax-react";
import { DespatchTask } from "Models/despatch.task";
import { TaskStartDatePeriod } from "Models/task";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BsThreeDotsVertical as IconDotVertical } from "react-icons/bs";
import { formatDecimalToWhole, handleGetConvertValue } from "Util/converts";
import { TaskType as TaskTypeEnum } from "Util/enums";
import { getCompanyColorAndBackgroundColorById } from "Util/getCompanyColorByName";
import { getMeasurement, getWeightMeasurement } from "Util/getMeasurement";
import { getUserTimeFormatPattern } from "Util/dateFormaterPreference";
import { getCompleteDateRangeLabel } from "Util/startDate";
import { TaskDescription } from "../../../Viewers/ResourceTaskViewer/TaskCard/styles";
import {
  Address,
  Badge,
  CardChip,
  CommentsIconWrapper,
  Container,
  Content,
  ContentHeader,
  CustomerName,
  DateRange,
  Footer,
  Header,
  MemberLeaderLabel,
  MemberLeaderNameBadge,
  MemberLeaderWrapper,
  QuantityValue,
  TaskType,
  Title,
} from "./styles";
import { useAppState } from "Context/StateContext";

const messageIcon = require("Assets/icons/message.png");

interface ResourceStorageBoxCardProps {
  task: DespatchTask;
  disabledTime?: boolean;
  isInResourceSection?: boolean;

  fullWidth?: boolean;
  rounded?: boolean;
  onMoreActionsClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const isSameDate = (from: string, to: string) => {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  const isEqualDate = fromDate.getDate() === toDate.getDate();
  const isEqualMonth = fromDate.getMonth() === toDate.getMonth();
  const isEqualYear = fromDate.getFullYear() === toDate.getFullYear();

  return isEqualDate && isEqualMonth && isEqualYear;
};

const isDestinationTaskType = (type?: string) =>
  !!type?.toLowerCase().trim() &&
  DESTINATION_TASK_TYPES.some(
    (destinationType) => destinationType.toLowerCase().trim() === type.toLowerCase().trim()
  );

export const ResourceOrderCard: React.FC<ResourceStorageBoxCardProps> = ({
  task,
  disabledTime = false,
  isInResourceSection = false,
  rounded,
  fullWidth,
  onMoreActionsClick,
}) => {
  const { t: translate } = useTranslation();
  const measurement = getMeasurement(task.company.measurement);
  const weightMeasurement = getWeightMeasurement(task.company.measurementWeight);
  const { changeTimeRef, taskCommentRef, currentDate } = useOperationDespatchContext();
  const { currentUser, formatWithUserTimezone, userTimezone, defaultTimezone } = useAppState();
  const timezone = userTimezone?.name || userTimezone?.nameFormatted || defaultTimezone;

  const volumeLabel = `${handleGetConvertValue(
    task?.totalVolumeQuoted || task?.totalVolumeActual,
    measurement.code,
    measurement.decimalPlaces
  )} ${measurement.code}`;

  const weightQuotedAndActualLabel = `${formatDecimalToWhole(
    handleGetConvertValue(
      task?.totalWeightQuoted,
      weightMeasurement.code,
      weightMeasurement.decimalPlaces
    )
  )} ${weightMeasurement.code}/${formatDecimalToWhole(
    handleGetConvertValue(
      task?.totalWeightActual,
      weightMeasurement.code,
      weightMeasurement.decimalPlaces
    )
  )} ${weightMeasurement.code}`;

  const isVip = !!task.job.vipTreatment;

  const address = useMemo(() => {
    if (!task?.job) return null;

    const place = isDestinationTaskType(task?.type)
      ? task.job.destinations?.[0]
      : task.job.origins?.[0];

    return place?.longAddress;
  }, [task?.type, task?.job?.destinations, task?.job?.origins]);

  const customerInfo = useMemo(() => {
    return `${task?.job?.customer?.abbreviation || ""} ${
      task?.job?.customer?.fullName || ""
    }`.trim();
  }, [task]);

  const showTitle = useMemo(() => {
    return !task?.isPlaceholder;
  }, [task]);

  const showMeasurements = useMemo(() => {
    return !task?.isPlaceholder;
  }, [task]);

  const statusValue = useMemo(() => {
    if (task?.isPlaceholder) return translate("placeholder");

    return task?.status || "Registered";
  }, [task]);

  const taskName = useMemo(() => {
    if (task?.isPlaceholder) return task?.name;

    return task?.type;
  }, [task]);

  const showLiftvanOrItemQuantity = useMemo(() => {
    const companyHasToShowLiftvan = !!GO_SHARE_COMPANY_IDS.includes(task?.companyId!);
    if (companyHasToShowLiftvan) {
      return `${task?.totalLiftvans} ${translate("lv")}`;
    }

    return `${task?.totalItems} ${translate("qty")}`;
  }, [task]);

  const renderDateOrTimeSection = () => {
    if (!task || !task.startDate) return null;

    let datesAreTheSame = true;

    if (task?.endDate) {
      datesAreTheSame = isSameDate(task.startDate, task.endDate);
    }

    const periodRanged = getCompleteDateRangeLabel({
      startDate: task.startDate,
      endDate: task.endDate,
      separator: "-",
      period: task.period,
      currentDate,
      timeSlots: task.timeSlots,
      timezone: userTimezone,
      preferences: currentUser?.preferences,
    });

    const Icon = datesAreTheSame ? IconClock : IconCalendar;

    const isMultiDay = task.period === TaskStartDatePeriod.MULTI_DAY;
    const separatorIndex = isMultiDay ? periodRanged.lastIndexOf(" (") : -1;
    const dateLine = separatorIndex !== -1 ? periodRanged.slice(0, separatorIndex) : periodRanged;
    const timeLine = separatorIndex !== -1 ? periodRanged.slice(separatorIndex + 2).replace(/^\(|\)$/g, "") : "";

    return (
      <DateRange
        disabled={disabledTime}
        onClick={() => {
          if (disabledTime) return null;

          changeTimeRef.current?.open(task);
        }}
      >
        <Icon size={16} />
        {isMultiDay && timeLine ? (
          <span>
            <span style={{ display: "block" }}>{dateLine}</span>
            <span style={{ display: "block" }}>{timeLine}</span>
          </span>
        ) : (
          <span>{periodRanged}</span>
        )}
      </DateRange>
    );
  };

  const renderTimezoneWarning = useMemo(() => {
    if (!task || !task.startDate || !task.endDate) return null;

    let datesAreTheSame = true;

    if (task?.endDate) {
      datesAreTheSame = isSameDate(task.startDate, task.endDate);
    }

    if (!datesAreTheSame) return null;

    const timePattern = getUserTimeFormatPattern(currentUser?.preferences);
    const endDateFormatted = formatWithUserTimezone(task.endDate, timePattern);
    const startDateFormatted = formatWithUserTimezone(task.startDate, timePattern);

    if (
      datesAreTheSame &&
      currentUser?.preferences?.timeFormat === "12_HOUR" &&
      endDateFormatted.toLowerCase().includes("am") &&
      startDateFormatted.toLowerCase().includes("pm")
    ) {
      return (
        <Header
          sx={{
            backgroundColor: colors.error100,
          }}
        >
          <span style={{ color: colors.error500 }}>{translate("timezoneIssue")}</span>
        </Header>
      );
    }

    return null;
  }, [task.endDate, task.startDate]);

  const renderLeaderSection = useMemo(() => {
    if (isInResourceSection || !task.memberLeaderId) return null;

    return (
      <MemberLeaderWrapper>
        <MemberLeaderLabel>{translate("leader")}:</MemberLeaderLabel>
        <MemberLeaderNameBadge>
          <span>{task.memberLeader?.fullName}</span>
        </MemberLeaderNameBadge>
      </MemberLeaderWrapper>
    );
  }, [task, task.memberLeader]);

  return (
    <Container fullWidth={fullWidth} rounded={rounded}>
      <Header
        sx={{
          backgroundColor: getCompanyColorAndBackgroundColorById(task.companyId, {
            isVip: false, // isVip,
            isPlaceholder: task?.isPlaceholder,
          })?.backgroundColor,
        }}
      >
        <CircleIcon
          sx={{
            width: "8px !important",
            height: "8px !important",
            color: getCompanyColorAndBackgroundColorById(task.companyId, {
              isVip: false, // isVip,
              isPlaceholder: task?.isPlaceholder,
            })?.color,
          }}
        />
        {task.company.name}
      </Header>
      <Divider />
      {showTitle ? (
        <Title isVip={false}>
          <IconShip size={16} />

          <span>
            {translate("order")} #{task.job.reference}
          </span>

          {isVip && <VipChip vipTreatment={task.job.vipTreatment!} />}
        </Title>
      ) : null}
      {renderTimezoneWarning}
      <Content>
        <ContentHeader>
          <TaskType text={taskName}>{taskName}</TaskType>

          <Badge>
            <BadgeJobStatus value={statusValue} />
          </Badge>
        </ContentHeader>

        {task.job?.jobType && <CardChip>{task.job.jobType.name}</CardChip>}

        <CustomerName>{customerInfo}</CustomerName>

        <Address>{address}</Address>
        <TaskDescription>{task?.description}</TaskDescription>

        {renderLeaderSection}

        {!isInResourceSection && task.job?.branchFrom && (
          <MemberLeaderWrapper>
            <MemberLeaderLabel>{translate("branchOrigin")}:</MemberLeaderLabel>
            <MemberLeaderNameBadge>
              <span>{task.job.branchFrom.name}</span>
            </MemberLeaderNameBadge>
          </MemberLeaderWrapper>
        )}

        {!isInResourceSection && task.job?.toBranch && (
          <MemberLeaderWrapper>
            <MemberLeaderLabel>{translate("branchDestination")}:</MemberLeaderLabel>
            <MemberLeaderNameBadge>
              <span>{task.job.toBranch.name}</span>
            </MemberLeaderNameBadge>
          </MemberLeaderWrapper>
        )}

        {!isInResourceSection && task.job?.branchFrom && (
          <MemberLeaderWrapper>
            <MemberLeaderLabel>{translate("branchOrigin")}:</MemberLeaderLabel>
            <MemberLeaderNameBadge>
              <span>{task.job.branchFrom.name}</span>
            </MemberLeaderNameBadge>
          </MemberLeaderWrapper>
        )}

        {!isInResourceSection && task.job?.toBranch && (
          <MemberLeaderWrapper>
            <MemberLeaderLabel>{translate("branchDestination")}:</MemberLeaderLabel>
            <MemberLeaderNameBadge>
              <span>{task.job.toBranch.name}</span>
            </MemberLeaderNameBadge>
          </MemberLeaderWrapper>
        )}

        {showMeasurements ? (
          <QuantityValue>
            {volumeLabel} • {weightQuotedAndActualLabel} • {showLiftvanOrItemQuantity}
          </QuantityValue>
        ) : null}
        <Footer>
          {renderDateOrTimeSection()}
          <div>
            <DefaultTooltip title={translate("comments")}>
              <IconButton
                onClick={() => {
                  taskCommentRef.current?.open(task);
                }}
              >
                <CommentsIconWrapper>
                  <img src={messageIcon} alt="messageIcon" />
                  {task?.comments && task?.comments?.length > 0 && (
                    <CommentsCount topMargin="-5px">{task.comments.length}</CommentsCount>
                  )}
                </CommentsIconWrapper>
              </IconButton>
            </DefaultTooltip>
            {onMoreActionsClick && (
              <IconButton onClick={onMoreActionsClick}>
                <IconDotVertical size={20} color={colors.gray600} />
              </IconButton>
            )}
          </div>
        </Footer>
      </Content>
    </Container>
  );
};
